import React, { useState } from "react";
import { useEditorStore } from "../../../../../state/editorStore";
import { ShadowToolIcon } from "../../../../../icons";
import ToolPrepSliderField from "../../../shared/ToolPrepSliderField";
import "../../../shared/ToolPrepObjectControls.css";
import styles from "./Shadow.module.css";

const DEFAULT_SHADOW = {
  enabled: false,
  x: 0,
  y: 0,
  blur: 0,
  opacity: 1,
  color: "#000000"
};

const isDefaultShadow = (shadow) =>
  shadow.x === 0 &&
  shadow.y === 0 &&
  shadow.blur === 0 &&
  shadow.opacity === 1 &&
  shadow.color === "#000000";

const Shadow = ({ defaultExpanded = false, forceExpanded = false }) => {
  const shadow = useEditorStore((s) => s.object.shadow);
  const setShadow = useEditorStore((s) => s.setShadow);
  const pushSnapshot = useEditorStore((s) => s.pushSnapshot);

  const [expanded, setExpanded] = useState(
    Boolean(defaultExpanded || forceExpanded)
  );
  const isExpanded = forceExpanded || expanded;

  /* =========================
     RECENT COLORS STATE
  ========================== */

  const [recentColors, setRecentColors] = useState(() => {
    const saved = localStorage.getItem("recentShadowColors");
    return saved ? JSON.parse(saved) : [];
  });

  const addRecentColor = (color) => {
    setRecentColors((prev) => {
      const updated = [
        color,
        ...prev.filter((c) => c !== color)
      ].slice(0, 7);

      localStorage.setItem(
        "recentShadowColors",
        JSON.stringify(updated)
      );

      return updated;
    });
  };

  /* =========================
     UPDATE SHADOW
  ========================== */

  const updateShadow = (updates, options = {}) => {
    const newShadow = {
      ...shadow,
      ...updates,
      enabled: true
    };

    if (isDefaultShadow(newShadow)) {
      setShadow(DEFAULT_SHADOW, options);
    } else {
      setShadow(newShadow, options);
    }
  };

  const commitSliderChange = () => {
    pushSnapshot();
  };

  const prepLayout = forceExpanded;

  const colorSection = (
    <>
      <div className={styles.colorOptions}>
        {[
          "#000000",
          "#444444",
          "#ffffff",
          "#3b82f6",
          "#ef4444",
          "#a855f7"
        ].map((color) => (
          <button
            key={color}
            className={`${styles.colorCircle} ${
              shadow.color === color ? styles.activeColor : ""
            }`}
            style={{ background: color }}
            onClick={(e) => {
              e.stopPropagation();
              updateShadow({ color });
              addRecentColor(color);
            }}
          />
        ))}

        <label
          className={styles.colorPickerWrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className={styles.colorPickerInner}
            style={{ background: shadow.color }}
          />

          <input
            type="color"
            value={shadow.color}
            onChange={(e) => {
              const newColor = e.target.value;
              updateShadow({ color: newColor });
              addRecentColor(newColor);
            }}
            className={styles.colorPicker}
          />
        </label>
      </div>

      {recentColors.length > 0 ? (
        <div className={styles.recentSection}>
          <div className={styles.recentLabel}>Recent</div>
          <div className={styles.recentColors}>
            {recentColors.map((color) => (
              <button
                key={color}
                className={styles.recentCircle}
                style={{ background: color }}
                onClick={(e) => {
                  e.stopPropagation();
                  updateShadow({ color });
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <div
      className={`${styles.card}${prepLayout ? ` ${styles.prepCard}` : ""}`}
      onClick={() => {
        if (!forceExpanded) setExpanded((prev) => !prev);
      }}
    >
      {!prepLayout ? (
        <div className={styles.header}>
          <div className={styles.left}>
            <span className={styles.icon}>
              <ShadowToolIcon />
            </span>
            <span className={styles.title}>Shadow</span>
          </div>

          <div className={styles.right}>
            <label
              className={styles.switch}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={shadow.enabled}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setShadow(DEFAULT_SHADOW);
                  } else {
                    setShadow({
                      ...shadow,
                      enabled: true
                    });
                  }
                }}
              />
              <span className={styles.slider}></span>
            </label>

            <button
              type="button"
              className={`${styles.chevron} ${
                isExpanded ? styles.rotate : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse shadow" : "Expand shadow"}
            >
              ›
            </button>
          </div>
        </div>
      ) : null}

      {/* BODY */}
      {isExpanded && (
        <div
          className={`${styles.body}${prepLayout ? ` ${styles.prepBody}` : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {prepLayout ? (
            <div className="crop-prep-controls object-prep-controls">
              <ToolPrepSliderField
                label="Horizontal"
                value={shadow.x}
                min={-100}
                max={100}
                unit="px"
                allowNegative
                onChange={(v) => updateShadow({ x: v }, { recordHistory: false })}
                onCommit={commitSliderChange}
              />
              <ToolPrepSliderField
                label="Vertical"
                value={shadow.y}
                min={-100}
                max={100}
                unit="px"
                allowNegative
                onChange={(v) => updateShadow({ y: v }, { recordHistory: false })}
                onCommit={commitSliderChange}
              />
              <ToolPrepSliderField
                label="Blur"
                value={shadow.blur}
                min={0}
                max={100}
                unit="px"
                onChange={(v) =>
                  updateShadow({ blur: v }, { recordHistory: false })
                }
                onCommit={commitSliderChange}
              />
              <ToolPrepSliderField
                label="Opacity"
                value={Math.round(shadow.opacity * 100)}
                min={0}
                max={100}
                unit="%"
                maxInputLength={3}
                onChange={(v) =>
                  updateShadow({ opacity: v / 100 }, { recordHistory: false })
                }
                onCommit={commitSliderChange}
              />

              <div className="crop-prep-controls__section">
                <span className="crop-prep-controls__section-title">Color</span>
                {colorSection}
              </div>
            </div>
          ) : (
            <>
              <Slider
                label="Horizontal"
                value={shadow.x}
                min={-100}
                max={100}
                unit="px"
                onChange={(v) => updateShadow({ x: v }, { recordHistory: false })}
                onDragStart={commitSliderChange}
              />
              <Slider
                label="Vertical"
                value={shadow.y}
                min={-100}
                max={100}
                unit="px"
                onChange={(v) => updateShadow({ y: v }, { recordHistory: false })}
                onDragStart={commitSliderChange}
              />
              <Slider
                label="Blur"
                value={shadow.blur}
                min={0}
                max={100}
                unit="px"
                onChange={(v) =>
                  updateShadow({ blur: v }, { recordHistory: false })
                }
                onDragStart={commitSliderChange}
              />
              <Slider
                label="Opacity"
                value={Math.round(shadow.opacity * 100)}
                min={0}
                max={100}
                unit="%"
                onChange={(v) =>
                  updateShadow({ opacity: v / 100 }, { recordHistory: false })
                }
                onDragStart={commitSliderChange}
              />

              <div className={styles.colorSection}>
                <div className={styles.colorLabel}>Color</div>
                {colorSection}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Slider = ({ label, value, min, max, unit, onChange, onDragStart }) => (
  <div className={styles.sliderRow}>
    <div className={styles.sliderTop}>
      <span>{label}</span>
      <span>
        {value} {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) =>
        onChange(Number(e.target.value))
      }
      onPointerDown={() => onDragStart?.()}
      className={styles.range}
    />
  </div>
);

export default Shadow;